import { useEffect, useRef, useState } from 'react';

import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  IconButton,
  Radio,
  RadioGroup,
  TextInput,
} from '@cfa/react-core';

import { cn } from '@/utils/tailwind';
import { validateTextField } from '@/utils/validateTextField';

import {
  addEmptyAnswer,
  oppositeLanguage,
  patchAnswerText,
  type QuizLocale,
  removeAnswer,
  setCorrectAnswer,
  type SetV2QuizEditorState,
  type V2QuizEditorState,
} from '../state';
import { MAX_V2_QUIZ_TEXT_LENGTH, translateText } from '../translateText';
import { isTrueFalseQuestion } from '../v2QuizValidation';

/**
 * One-shot autosave indicator (see `.quiz-autosave*` in index.css).
 * Parent starts `isAnimating` on isSaving (same-component render setState).
 * Runs the full CSS timeline even if save finishes early, and ignores
 * overlapping saves until fade-out completes.
 */
const AutosavingLabel = ({
  isAnimating,
  setIsAnimating,
}: {
  isAnimating: boolean;
  setIsAnimating: (value: boolean) => void;
}) => {
  const { t } = useTranslation();

  if (!isAnimating) {
    return (
      <span
        className="text-grayscale-gray6 ml-auto shrink-0 text-sm opacity-0"
        data-testid="QuizQuestionSaving"
        aria-hidden
      >
        {/* Hold line-box height so the footer does not jump when text appears. */}
        {'\u00a0'}
      </span>
    );
  }

  return (
    <span
      className="quiz-autosave text-grayscale-gray6 ml-auto shrink-0 text-sm"
      data-testid="QuizQuestionSaving"
      onAnimationEnd={event => {
        if (
          event.target !== event.currentTarget ||
          event.animationName !== 'quiz-autosave-fade-out'
        ) {
          return;
        }
        setIsAnimating(false);
      }}
    >
      <span className="quiz-autosave__label">
        {t('TrainingPlans.buildQuizzes.saving')}
      </span>
      <span className="quiz-autosave__dot">.</span>
      <span className="quiz-autosave__dot">.</span>
      <span className="quiz-autosave__dot">.</span>
    </span>
  );
};

interface BuildQuizzesAnswersV2Props {
  questionIndex: number;
  quiz: V2QuizEditorState;
  setQuiz: SetV2QuizEditorState;
  /** userPrefs language — not `quiz.primaryLanguage`. */
  userLanguage: QuizLocale;
  isSaving: boolean;
  validationMode: boolean;
  trySave: (state: V2QuizEditorState, questionIndex: number | null) => void;
  registerTranslate: (key: string, abort: AbortController) => void;
  clearTranslate: (key: string, abort: AbortController) => void;
  onAnswerFieldBlur?: () => void;
  onCorrectAnswerSelected?: () => void;
  isDisplayLanguageMismatch?: boolean;
}

const BuildQuizzesAnswersV2: React.FC<BuildQuizzesAnswersV2Props> = ({
  questionIndex,
  quiz,
  setQuiz,
  userLanguage,
  isSaving,
  validationMode,
  trySave,
  registerTranslate,
  clearTranslate,
  onAnswerFieldBlur,
  onCorrectAnswerSelected,
  isDisplayLanguageMismatch = false,
}) => {
  const { t } = useTranslation();
  const question = quiz.questions[questionIndex];
  const isTrueFalse = question ? isTrueFalseQuestion(question) : false;
  const selectedAnswerIndex =
    question?.answers.findIndex(answer => answer.correct) ?? -1;
  // Field-level: only show empty-answer errors after that option was blurred.
  const [touchedAnswerIndexes, setTouchedAnswerIndexes] = useState(
    () => new Set<number>(),
  );
  // Shared with the True/False footer `invisible` toggle so a short save does not hide
  // the row mid-animation (MC keeps the row via Add Option).
  const [isAutosaveAnimating, setIsAutosaveAnimating] = useState(false);
  // After `await`, do not capture next state from inside setState updaters.
  const quizRef = useRef(quiz);
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  const handleOptionText = (value: string, optionIndex: number) => {
    const other = oppositeLanguage(userLanguage);
    setQuiz(prev =>
      patchAnswerText(prev, questionIndex, optionIndex, {
        [userLanguage]: value,
        [other]: '',
      }),
    );
  };

  const handleAnswerBlur = async (optionIndex: number, rawValue: string) => {
    const clientId = question?.clientId;
    if (isTrueFalse) {
      return;
    }

    setTouchedAnswerIndexes(prev => {
      const next = new Set(prev);
      next.add(optionIndex);
      return next;
    });
    onAnswerFieldBlur?.();

    // Prefer the DOM value so save does not validate a stale empty answer.
    const primary = rawValue.trim();
    const other = oppositeLanguage(userLanguage);
    if (!primary) {
      if (clientId) {
        const translateKey = `answer:${clientId}:${optionIndex}`;
        const staleAbort = new AbortController();
        staleAbort.abort();
        registerTranslate(translateKey, staleAbort);
        clearTranslate(translateKey, staleAbort);
      }
      setQuiz(prev =>
        patchAnswerText(prev, questionIndex, optionIndex, {
          [userLanguage]: '',
          [other]: '',
        }),
      );
      return;
    }

    if (!clientId) {
      return;
    }

    const translateKey = `answer:${clientId}:${optionIndex}`;
    const abort = new AbortController();
    registerTranslate(translateKey, abort);

    try {
      const translated = await translateText(primary, userLanguage).catch(
        () => null,
      );
      if (abort.signal.aborted) {
        return;
      }

      const currentIndex = quizRef.current.questions.findIndex(
        item => item.clientId === clientId,
      );
      if (currentIndex < 0) {
        return;
      }

      const currentQuestion = quizRef.current.questions[currentIndex];
      const answers = currentQuestion?.answers;
      if (!answers) {
        return;
      }
      // Prefer the blurred index so duplicate answer text does not retarget
      // the first match and leave this option untranslated.
      const textAtBlurredIndex = (
        answers[optionIndex]?.answer[userLanguage] ?? ''
      ).trim();
      const optionIndexNow =
        textAtBlurredIndex === primary
          ? optionIndex
          : answers.findIndex(
              answer => (answer.answer[userLanguage] ?? '').trim() === primary,
            );
      if (optionIndexNow < 0) {
        return;
      }

      if (translated == null) {
        // Drop the old translation so it is never saved with the new text;
        // trySave retries the missing translation and surfaces a failure.
        const next = patchAnswerText(
          quizRef.current,
          currentIndex,
          optionIndexNow,
          { [other]: '' },
        );
        quizRef.current = next;
        setQuiz(next);
        clearTranslate(translateKey, abort);
        trySave(next, currentIndex);
        return;
      }

      const nextForSave = patchAnswerText(
        quizRef.current,
        currentIndex,
        optionIndexNow,
        {
          [userLanguage]: primary,
          [other]: translated,
        },
      );
      quizRef.current = nextForSave;
      setQuiz(nextForSave);
      clearTranslate(translateKey, abort);
      trySave(nextForSave, currentIndex);
    } finally {
      clearTranslate(translateKey, abort);
    }
  };

  const handleCorrectOption = (optionIndex: number) => {
    const nextForSave = setCorrectAnswer(
      quizRef.current,
      questionIndex,
      optionIndex,
    );
    quizRef.current = nextForSave;
    setQuiz(nextForSave);
    onCorrectAnswerSelected?.();
    trySave(nextForSave, questionIndex);
  };

  const addOption = () => {
    setQuiz(prev => addEmptyAnswer(prev, questionIndex));
  };

  const deleteOption = (optionIndex: number) => {
    setTouchedAnswerIndexes(prev => {
      const next = new Set<number>();
      for (const index of prev) {
        if (index === optionIndex) continue;
        next.add(index > optionIndex ? index - 1 : index);
      }
      return next;
    });
    const clientId = question?.clientId;
    const currentIndex = quizRef.current.questions.findIndex(
      item => item.clientId === clientId,
    );
    if (currentIndex < 0) {
      return;
    }
    const next = removeAnswer(quizRef.current, currentIndex, optionIndex);
    quizRef.current = next;
    setQuiz(next);
    trySave(next, currentIndex);
  };

  if (!question) {
    return null;
  }

  // Hold the autosave cycle for the full CSS duration; skip restart while playing.
  // Same-component render setState (not in AutosavingLabel — that would update parent mid-render).
  if (isSaving && !isAutosaveAnimating) {
    setIsAutosaveAnimating(true);
  }

  return (
    <div className="mx-0 my-4 text-left">
      <RadioGroup
        value={selectedAnswerIndex >= 0 ? String(selectedAnswerIndex) : ''}
        orientation="vertical"
        onChange={value => handleCorrectOption(Number(value))}
        isDisabled={isDisplayLanguageMismatch}
        className="w-full!"
      >
        {question.answers.map((option, optionIndex) => (
          <div
            className={cn(
              'flex rounded border border-solid border-transparent pt-2',
              {
                'border-feedback-success-base bg-feedback-success-container':
                  option.correct,
              },
            )}
            key={`questionOption-${optionIndex}`}
          >
            <Radio
              className={cn(
                'text-grayscale-gray4 [&::before]:border-1.5! [&::before]:border-grayscale-gray3! -mt-1 flex cursor-pointer self-center pr-2 pl-1 [&::before]:size-6 [&::before]:border-solid!',
                {
                  '[&::before]:border-feedback-success-base! [&::before]:border-1.5! [&::before]:border-solid!':
                    option.correct,
                },
              )}
              value={String(optionIndex)}
            />
            <div className={cn('relative min-h-[94px]! w-full pr-2')}>
              <TextInput
                className="[&_.cfa-input-label]:text-sm! [&_.cfa-input-label]:font-semibold! [&_input]:text-sm!"
                data-testid={`EnterAnswer${optionIndex + 1}`}
                errorMessage={
                  !isTrueFalse &&
                  !validateTextField(option.answer[userLanguage] ?? '') &&
                  (touchedAnswerIndexes.has(optionIndex) || validationMode)
                    ? t('TrainingPlans.buildQuizzes.errorQuestion')
                    : ''
                }
                label={`${t('TrainingPlans.buildQuizzes.option')} ${
                  optionIndex + 1
                }`}
                maxLength={MAX_V2_QUIZ_TEXT_LENGTH}
                onChange={value => handleOptionText(value, optionIndex)}
                onBlur={e => {
                  void handleAnswerBlur(optionIndex, e.target.value);
                }}
                // RadioGroup arrow-key nav must not steal caret movement in the field.
                onKeyDown={event => {
                  if (
                    event.key === 'ArrowLeft' ||
                    event.key === 'ArrowRight' ||
                    event.key === 'ArrowUp' ||
                    event.key === 'ArrowDown'
                  ) {
                    event.stopPropagation();
                  }
                }}
                placeholder={t('TrainingPlans.buildQuizzes.enterAnswer')}
                isRequired
                isDisabled={isTrueFalse || isDisplayLanguageMismatch}
                value={option.answer[userLanguage] ?? ''}
              />
              {option.correct && (
                <div>
                  <IconCheck
                    size={20}
                    className="text-feedback-success-base! absolute! top-[calc(50%-10px)]! right-4!"
                  />
                </div>
              )}
              {isDisplayLanguageMismatch && (
                <div
                  aria-hidden
                  data-testid={`AnswerHoverTitle${optionIndex + 1}`}
                  className="pointer-events-auto absolute inset-0 cursor-not-allowed"
                  title={option.answer[userLanguage] ?? ''}
                />
              )}
            </div>
            {!isTrueFalse && question.answers.length > 2 && (
              <div className="mt-6 mb-0 ml-3 flex h-12 items-center pr-2">
                <IconButton
                  color="secondary"
                  className="mr-2 p-0!"
                  aria-label={`${t('Button.remove')} ${t(
                    'TrainingPlans.buildQuizzes.option',
                  )} ${optionIndex + 1}`}
                  isDisabled={isDisplayLanguageMismatch}
                  onPress={() => deleteOption(optionIndex)}
                >
                  <IconX size={26} className="text-grayscale-gray6!" />
                </IconButton>
              </div>
            )}
          </div>
        ))}
      </RadioGroup>
      <div
        className={cn(
          'mt-2 flex items-center justify-between gap-4',
          // TF has no Add Option — hide the empty footer when idle, but keep it
          // visible for the full autosave animation (not only while isSaving).
          isTrueFalse && !isAutosaveAnimating && 'invisible',
        )}
      >
        {!isTrueFalse && (
          <Button
            className="ml-4! gap-2!"
            color="secondary"
            isDisabled={isDisplayLanguageMismatch}
            onPress={addOption}
            size="lg"
            variant="outlined"
          >
            <IconPlus size={20} />
            {t('Button.addOption')}
          </Button>
        )}
        <AutosavingLabel
          isAnimating={isAutosaveAnimating}
          setIsAnimating={setIsAutosaveAnimating}
        />
      </div>
    </div>
  );
};

export default BuildQuizzesAnswersV2;
